/**
 * RecentRecordsWidget — ostatnio dodane rekordy wybranej encji (per aktywna
 * firma). Minimalny kształt (id/label/createdAt, zero PII) prosto z backendu.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useActiveCompanyScope } from '@/lib/use-active-company';

import { classifyDataError, fetchRecent, type RecentRow } from '../dashboard-api';
import type { WidgetRenderProps } from '../types';

import { WidgetDataError, WidgetEmpty, WidgetLoading } from './WidgetState';

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pl-PL', { dateStyle: 'short' });
}

export function RecentRecordsWidget({ widget }: WidgetRenderProps) {
  const { t } = useTranslation();
  const { isAll, companyId } = useActiveCompanyScope();
  const entity = widget.type === 'recent_records' ? widget.config.entity : null;
  const limit = widget.type === 'recent_records' ? (widget.config.limit ?? 5) : 5;
  // Wszystkie encje tego kafelka są per-firma (COMPANY_ENTITIES) — w trybie
  // "Wszystkie firmy" krótko spinamy zamiast wysyłać zapytanie skazane na 400
  // MISSING_HEADER.
  const blockedByAllMode = entity !== null && isAll;

  // Scope firmy w kluczu — defense-in-depth: dziś przełącznik firmy odświeża
  // kafelki blankietowym invalidateQueries w CompanySwitcher, ale bez firmy w
  // queryKey przetrwały cache dałby dane poprzedniej firmy, gdyby to
  // invalidateQueries kiedyś zniknęło albo się prześcignęło z fetchem.
  const query = useQuery({
    queryKey: ['tenant', 'dashboard', 'recent', entity, limit, companyId ?? 'all'],
    queryFn: () => fetchRecent(entity!, limit),
    enabled: entity !== null && !blockedByAllMode,
    staleTime: 30_000,
    retry: false,
  });

  if (widget.type !== 'recent_records') return null;

  if (blockedByAllMode) return <WidgetEmpty message={t('tenant.dashboard.pickCompany')} />;
  if (query.isLoading) return <WidgetLoading />;
  if (query.isError)
    return (
      <WidgetDataError kind={classifyDataError(query.error)} onRetry={() => query.refetch()} />
    );

  const rows: RecentRow[] = query.data ?? [];
  if (rows.length === 0)
    return <WidgetEmpty message={t('tenant.dashboard.widgets.recent_records.empty')} />;

  return (
    <ul className="flex flex-col divide-y divide-border-secondary">
      {rows.map((r) => (
        <li key={r.id} className="flex items-baseline justify-between gap-2 py-1.5">
          <span className="min-w-0 truncate text-sm text-text-primary" title={r.label}>
            {r.label || '—'}
          </span>
          <span className="shrink-0 text-[11px] tabular-nums text-text-muted">
            {formatDay(r.createdAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}
